/**
 * §ai-on-off — the tenant-visible AI on/off switch.
 *
 * A persistent pill in the AppShell sidebar footer showing whether the agent
 * is answering customers (AI: On, green) or paused (AI: Off, grey). Clicking
 * flips the ONE source of truth — the `agent_published` flag in
 * tenant_settings (same flag the onboarding "Go live" button and Mission
 * Control's per-account switch write) — via the existing
 * /api/admin/agent/go-live endpoint. Turning OFF asks for confirmation,
 * because it silences the agent on EVERY customer channel at once.
 *
 * Owner dashboard access is never affected — this only stops the agent
 * answering customers. Grandfathered tenants (no flag) read as published.
 *
 * Fail-silent chrome like GoLiveBanner: while loading or on a status-read
 * error the pill renders nothing rather than a wrong state. Operators
 * without settings.edit see the state but can't flip it.
 *
 * §v5.mobile-ai-state (2026-08-10) — on a phone the pill above lives in the
 * sidebar FOOTER, which below 768px is an off-canvas drawer: "is my AI
 * answering?" became a question you had to open a menu to answer. The
 * `AgentPowerStatus` export below is the answer at a glance — a dot + word in
 * the mobile top bar. It deliberately does NOT flip the flag: turning the
 * agent off silences every customer channel at once, and that decision keeps
 * its confirmation step in the drawer. Tapping the readout opens the drawer
 * (where the real switch is), so the indicator is a route to the control
 * rather than a second, thumb-sized copy of it.
 *
 * Both components read the SAME react-query key, so the drawer switch and the
 * top-bar readout can never disagree — one fetch, one cache entry, one truth.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getTenantId } from "@/lib/auth";
import { usePermissions } from "@/lib/permissions";
import {
  getAgentPublishStatus,
  setAgentGoLive,
} from "@/lib/onboardingChecklist";

const PILL: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "7px 10px",
  marginBottom: 8,
  borderRadius: 10,
  border: "1px solid var(--glass-hairline, rgba(255,255,255,0.10))",
  background: "transparent",
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--fg-2, inherit)",
  cursor: "pointer",
  textAlign: "left",
};
const DOT = (on: boolean): React.CSSProperties => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  flexShrink: 0,
  background: on ? "var(--success, #3fb26f)" : "var(--fg-4, #888)",
  boxShadow: on
    ? "0 0 6px color-mix(in oklab, var(--success, #3fb26f) 70%, transparent)"
    : "none",
});

/** The one read of the publish flag. Shared by the drawer switch and the
 *  mobile top-bar readout so the two can never show different states. */
function useAgentPublishStatus() {
  const tenant = getTenantId();
  const q = useQuery({
    queryKey: ["agent", "publish-status"],
    queryFn: getAgentPublishStatus,
    // The flag can be flipped from Mission Control too — keep the pill honest.
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
    // The master plane has no customer agent of its own.
    enabled: tenant !== "master",
  });
  return { tenant, statusQ: q };
}

export function AgentPowerSwitch() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canEdit = has("settings.edit");
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { tenant, statusQ } = useAgentPublishStatus();

  const flipMut = useMutation({
    mutationFn: (published: boolean) => setAgentGoLive(published),
    onSuccess: (fresh) => {
      qc.setQueryData(["agent", "publish-status"], fresh);
      setConfirming(false);
      setError(null);
    },
    onError: () => setError("Couldn't update — please try again."),
  });

  // Fail-silent chrome: never show a guessed state.
  if (tenant === "master" || !statusQ.data) return null;

  const on = statusQ.data.published;

  const onClick = () => {
    if (!canEdit || flipMut.isPending) return;
    if (on) setConfirming(true);
    else flipMut.mutate(true);
  };

  return (
    <>
      <button
        type="button"
        style={{ ...PILL, cursor: canEdit ? "pointer" : "default" }}
        onClick={onClick}
        disabled={flipMut.isPending}
        aria-label={on ? "AI is on — click to turn off" : "AI is off — click to turn on"}
        title={
          canEdit
            ? on
              ? "Your assistant is answering customers. Click to turn it off."
              : "Your assistant is off. Click to turn it back on."
            : "AI answering state (ask an Owner/Admin to change it)"
        }
        data-testid="agent-power-switch"
        data-state={on ? "on" : "off"}
      >
        <span style={DOT(on)} aria-hidden />
        <span style={{ flex: 1 }}>
          AI: {flipMut.isPending ? "…" : on ? "On" : "Off"}
        </span>
        <span style={{ fontSize: 10.5, fontWeight: 500, opacity: 0.6 }}>
          {on ? "answering" : "paused"}
        </span>
      </button>
      {error && (
        <div style={{ fontSize: 11, color: "var(--danger, #c0392b)", marginBottom: 6 }}>
          {error}
        </div>
      )}
      {confirming && (
        <div
          role="dialog"
          data-testid="agent-power-confirm-dialog"
          aria-modal="true"
          aria-label="Turn AI off?"
          onClick={(e) => {
            if (e.target === e.currentTarget && !flipMut.isPending) setConfirming(false);
          }}
          /* §v5.mobile-sheet — bottom sheet on phones (thumb reaches the
             buttons), centred dialog from 768px up. */
          className="fixed inset-0 z-modal flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm md:items-center md:p-4"
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-t-2xl border border-border-subtle bg-surface-raised shadow-lg md:rounded-lg"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="border-b border-border-subtle px-5 py-4">
              <h2 className="text-lg font-semibold text-text-primary">Turn your AI off?</h2>
            </header>
            <div className="px-5 py-4 text-sm text-text-secondary">
              Your assistant will stop answering customers on every channel
              until you turn it back on. You keep full access to this
              dashboard, and you can turn it on again at any time.
            </div>
            {/* Full-width stacked buttons on a phone; the desktop right-aligned
                row returns at 768px. min-h-11 = the 44px touch floor. */}
            <div className="flex flex-col-reverse gap-2 px-5 pb-4 md:flex-row md:justify-end">
              <button
                type="button"
                className="btn btn-ghost min-h-11 w-full justify-center md:min-h-0 md:w-auto"
                onClick={() => setConfirming(false)}
                disabled={flipMut.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger min-h-11 w-full justify-center md:min-h-0 md:w-auto"
                data-testid="agent-power-confirm-off"
                onClick={() => flipMut.mutate(false)}
                disabled={flipMut.isPending}
              >
                {flipMut.isPending ? "Turning off…" : "Turn AI off"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * §v5.mobile-ai-state — compact "is the AI answering?" readout for the mobile
 * top bar. Same query as the switch above, so it cannot drift.
 *
 * Deliberately quiet when the agent is ON (a dot only — "working as expected"
 * is not news) and explicit when it is OFF, where the word "Off" is the whole
 * point: an owner glancing at their phone should not have to open a drawer to
 * discover their assistant has been silent all morning.
 *
 * Renders nothing when the status is unknown — same fail-silent contract as
 * the switch. Never shows a guessed state.
 */
export function AgentPowerStatus({ onOpen }: { onOpen: () => void }) {
  const { tenant, statusQ } = useAgentPublishStatus();

  if (tenant === "master" || !statusQ.data) return null;

  const on = statusQ.data.published;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="app-topbar-ai"
      data-testid="agent-power-status"
      data-state={on ? "on" : "off"}
      aria-label={
        on
          ? "AI is on and answering customers. Open the menu to change it."
          : "AI is off and not answering customers. Open the menu to turn it back on."
      }
    >
      <span style={DOT(on)} aria-hidden />
      {/* The label is for the OFF case; hidden (not removed) when on, so the
          accessible name above still carries the full state either way. */}
      {!on && <span className="app-topbar-ai-label">AI off</span>}
    </button>
  );
}
